// LoginModal.tsx
import React, { useState, useEffect, useRef, forwardRef, useCallback, useImperativeHandle } from "react";
import { Button, Input, InputRef, Modal, Space, Spin, Tooltip } from 'antd';
import { gsap } from 'gsap';

import '@login/styles/login.scss';

// "With country select" component.
import { CountryCode, E164Number } from "libphonenumber-js";

import 'react-phone-number-input/style.css';
import '@styles/brandStyles.css'

import { resetAuthTokenState, resetAuthTokenError, logout, resetAuthError } from '@store/slices/authSlice.tsx';
import { RootState, useAppDispatch, useAppSelector } from "@store/store.tsx";
import { getCountryCallingCode } from "react-phone-number-input";
import PhoneInput from '@tools/components/PhoneInput';
import { EmailLoginInput, WhatsAppLoginInput } from "@login/components/loginUtils";
import { SmartLoginForm } from "@login/components/SmartLoginForm";
import {
  MailOutlined,
  WhatsAppOutlined,
  UserOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { logInfo } from "@tools/LogsHandler.tsx";
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import { useNavigate, useLocation, Location } from 'react-router-dom';

// ============================
// EmailPhoneUsernameInput (sin cambios funcionales)
// ============================
type InputType = 'username' | 'email' | 'phone';

interface EmailPhoneUsernameInputProps {
  className?: string;
  style?: React.CSSProperties;
  onChange?: (value: string) => void;
  onInputTypeChange?: (inputType: InputType) => void;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  name?: string;
  id?: string;
  inputType?: InputType;
}

export const EmailPhoneUsernameInput = forwardRef<InputRef | null, EmailPhoneUsernameInputProps>(
  (
    {
      className,
      style,
      onChange,
      onInputTypeChange,
      placeholder,
      defaultValue,
      value: propValue,
      name,
      id,
      inputType: propInputType = 'username'
    },
    ref,
  ) => {
    const defaultCode = '+549';
    const [inputType, setInputType] = useState<InputType>(propInputType);
    const [inputValue, setInputValue] = useState<string | E164Number>(propValue || defaultValue || '');
    const [countryCode, setCountryCode] = useState<string>(defaultCode);

    // Refs for inputs
    const usernameInputRef = useRef<InputRef>(null);
    const emailInputRef = useRef<InputRef>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const phoneInputRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { resolvedTheme } = useThemeMode();
    const isDark = resolvedTheme === 'dark';
    const textMuted = isDark ? '#bfbfbf' : '#8c8c8c';
    const baseBorder = isDark ? '#434343' : '#d9d9d9';
    const dividerColor = isDark ? '#303030' : '#f0f0f0';

    useImperativeHandle<InputRef | null, InputRef | null>(
      ref,
      () => {
        if (inputType === 'phone') {
          return {
            focus: () => phoneInputRef.current?.focus?.(),
            blur: () => phoneInputRef.current?.blur?.(),
            select: () => phoneInputRef.current?.select?.(),
            input: phoneInputRef.current?.input ?? undefined,
          } as InputRef;
        }

        const activeRef = inputType === 'email' ? emailInputRef.current : usernameInputRef.current;
        return activeRef ?? null;
      },
      [inputType],
    );

    // Función para obtener el placeholder según el tipo
    const getPlaceholder = () => {
      switch (inputType) {
        case 'username': return placeholder || 'Usuario';
        case 'email': return placeholder || 'Email';
        case 'phone': return placeholder || 'Celular';
        default: return placeholder || 'Usuario';
      }
    };

    // Función para validar el valor según el tipo
    const validateValue = (type: InputType, value: string | E164Number) => {
      if (type === 'phone') {
        if (!/^[+\d]/.test(value as string)) {
          return countryCode;
        } else if (value && !(value as string).startsWith(countryCode)) {
          const adjusted = (value as string).startsWith('+54')
            ? `+549${(value as string).slice(3)}`
            : `${countryCode}${value}`;
          logInfo('EmailPhoneSwitchInput - validateValue - inputValue: ', adjusted, 'type: ', type);
          return adjusted;
        }
        return value;
      }
      return value === countryCode ? '' : value;
    };

    // Función para manejar el cambio de tipo de input
    const handleInputTypeChange = (newInputType: InputType) => {
      if (newInputType === inputType) return;

      // Animación de salida
      if (containerRef.current) {
        const field = containerRef.current.querySelector('.input-field');
        if (field) {
          gsap.to(field, {
            opacity: 0,
            x: -20,
            duration: 0.15,
            ease: 'power2.out',
            onComplete: () => {
              setInputType(newInputType);
              const validated = validateValue(newInputType, inputValue);
              setInputValue(validated as string);
              onInputTypeChange?.(newInputType);

              const newField = containerRef.current?.querySelector('.input-field');
              if (newField) {
                gsap.fromTo(
                  newField,
                  { opacity: 0, x: 20 },
                  { opacity: 1, x: 0, duration: 0.15, ease: 'power2.out' }
                );
              }
            }
          });
        } else {
          setInputType(newInputType);
          const validated = validateValue(newInputType, inputValue);
          setInputValue(validated as string);
          onInputTypeChange?.(newInputType);
        }
      } else {
        setInputType(newInputType);
        const validated = validateValue(newInputType, inputValue);
        setInputValue(validated as string);
        onInputTypeChange?.(newInputType);
      }
    };

    // Función para manejar cambios en input de texto (username/email)
    const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      onChange?.(value);
    };

    // Función para manejar cambios en phone input
    const handlePhoneChange = (value: E164Number | undefined) => {
      // No cambiar el tipo si el país está cambiando
      if (countryChangingRef.current) {
        countryChangingRef.current = false;
        return;
      }

      const validated = validateValue('phone', value || '') as string;
      setInputValue(validated);
      onChange?.(validated);
    };

    // Función para manejar cambio de país
    const countryChangingRef = useRef(false);
    const onCountryChange = (newCountryCode: CountryCode) => {
      countryChangingRef.current = true; // Indica que el país está cambiando

      if (newCountryCode) {
        const value = (inputValue as string)?.startsWith(countryCode)
          ? (inputValue as string).slice(countryCode.length)
          : inputValue || '';
        const newCode = newCountryCode === 'AR'
          ? `+${getCountryCallingCode(newCountryCode)}9`
          : `+${getCountryCallingCode(newCountryCode)}`;
        setCountryCode(newCode);
        setInputValue(`${newCode}${value}`);
      }
    };

    // Effect para enfocar el input correcto
    useEffect(() => {
      setTimeout(() => {
        if (inputType === 'phone') {
          phoneInputRef.current?.focus();
        } else if (inputType === 'email') {
          emailInputRef.current?.focus();
        } else {
          usernameInputRef.current?.focus();
        }
      }, 50);  // Reduced from 200ms for snappier UX
    }, [inputType]);

    // Configuración de iconos
    const iconConfig = [
      {
        type: 'username' as InputType,
        icon: <UserOutlined />,
        label: 'Usuario',
        color: '#1890ff'
      },
      {
        type: 'email' as InputType,
        icon: <MailOutlined />,
        label: 'Email',
        color: '#52c41a'
      },
      {
        type: 'phone' as InputType,
        icon: <PhoneOutlined />,
        label: 'WhatsApp',
        color: '#25D366'
      }
    ];

    return (
      <div ref={containerRef} style={{ ...style }} className={className}>
        {/* Selector de tipo de input */}
        <div
          className="d-flex justify-content-center align-items-center mt-3"
          style={{
            padding: '12px 16px',
            // background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            borderRadius: '8px'
          }}
        >
          {iconConfig.map((config, index) => (
            <React.Fragment key={config.type}>
              <Tooltip title={`Ingresar con ${config.label}`}>
                <button
                  type="button"
                  aria-label={`Ingresar con ${config.label}`}
                  aria-pressed={inputType === config.type}
                  onClick={() => handleInputTypeChange(config.type)}
                  className={`btn-icon-selector ${inputType === config.type ? 'active' : ''}`}
                  style={{
                    background: 'none',
                    border: '2px solid',
                    borderColor: inputType === config.type ? config.color : baseBorder,
                    borderRadius: '50%',
                    width: '45px',
                    height: '45px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    color: inputType === config.type ? config.color : textMuted,
                    fontSize: '18px',
                    transform: inputType === config.type ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: inputType === config.type ? `0 0 0 3px ${config.color}20` : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (inputType !== config.type) {
                      e.currentTarget.style.borderColor = config.color;
                      e.currentTarget.style.color = config.color;
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (inputType !== config.type) {
                      e.currentTarget.style.borderColor = baseBorder;
                      e.currentTarget.style.color = textMuted;
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {config.icon}
                </button>
              </Tooltip>
              {index < iconConfig.length - 1 && (
                <div style={{ width: '30px', height: '2px', background: dividerColor, margin: '0 15px' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Texto descriptivo */}
        <div className="text-center mt-2">
          <small style={{ color: textMuted, fontSize: '12px' }}>
            {inputType === 'username' && 'Ingresa tu nombre de usuario'}
            {inputType === 'email' && 'Ingresa tu dirección de correo electrónico'}
            {inputType === 'phone' && 'Ingresa tu número de WhatsApp'}
          </small>
        </div>

        {/* Input field */}
        <div className="input-field">
          {inputType === 'phone' ? (
            <PhoneInput
              placeholder={getPlaceholder()}
              value={inputValue as E164Number}
              onChange={handlePhoneChange}
              onCountryChange={onCountryChange}
              ref={phoneInputRef}
              defaultCountry="AR"
              id={id}
              name={name}
              autoComplete="tel"
              international
            />
          ) : (
            <Input
              placeholder={getPlaceholder()}
              value={inputValue as string}
              onChange={handleTextInputChange}
              ref={inputType === 'email' ? emailInputRef : usernameInputRef}
              id={id}
              name={name}
              type={inputType === 'email' ? 'email' : 'text'}
              autoComplete={inputType === 'email' ? 'email' : 'username'}
            />
          )}
        </div>

      </div>
    );
  }
);

EmailPhoneUsernameInput.displayName = 'EmailPhoneUsernameInput';

// ============================
// LoginContent (compartido por modal y página)
// ============================
type CurrentView =
  | 'login'
  | 'registerOptions'
  | 'whatsappLogin'
  | 'whatsappRegister'
  | 'emailRegister'
  | 'forgotPasswordOptions'
  | 'whatsappForgotPassword'
  | 'emailForgotPassword';

interface LoginContentProps {
  onClose?: () => void;
  /** Cambia para re-disparar la animación de entrada (ej: open del modal) */
  animateKey?: unknown;
  /** Si es true, dibuja encabezado interno (para página o para modal sin title) */
  showHeader?: boolean;
  /** Notifica el título dinámico al wrapper (para ponerlo en el Modal.title) */
  onTitleChange?: (title: string) => void;
}

const DEFAULT_BUTTON_SHAPE = 'default';
const DEFAULT_BUTTON_RADIUS = '8px';
// const { Text } = Typography;

const LoginContent: React.FC<LoginContentProps> = ({ onClose: _, animateKey, showHeader, onTitleChange }) => {
  const dispatch = useAppDispatch();
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';
  const radius = DEFAULT_BUTTON_RADIUS;
  const authStatus = useAppSelector((state: RootState) => state.auth.status);

  // Estado para manejar la vista actual
  const [currentView, setCurrentView] = useState<CurrentView>('login');

  const loginContainerRef = useRef<HTMLDivElement>(null);

  // const handleAfterClose = () => {
  //   formRef.current?.resetFields();
  //   setCurrentView('login');
  //   setInputType('username');
  //   setInputValue('');
  //   dispatch(resetAuthTokenState());
  //   dispatch(resetAuthTokenError());
  // };

  // Animación de entrada (al abrir modal o montar página)
  useEffect(() => {
    if (loginContainerRef.current) {
      gsap.fromTo(
        loginContainerRef.current,
        { opacity: 0, scale: 0.9, y: -30 },
        { opacity: 1, scale: 1, y: 0, duration: 0.4, ease: 'back.out(1.7)' }
      );
    }
  }, [animateKey]);

  // Pulso sutil cuando status === loading (con cleanup correcto)
  useEffect(() => {
    let tween: gsap.core.Tween | undefined;
    if (authStatus === 'loading' && loginContainerRef.current) {
      tween = gsap.to(loginContainerRef.current, {
        opacity: 0.8,
        repeat: -1,
        yoyo: true,
        duration: 0.6,
      });
    }
    return () => { if (tween) tween.kill(); };
  }, [authStatus]);

  // Reset global al desmontar (útil para versión página)
  useEffect(() => {
    return () => {
      dispatch(resetAuthTokenState());
      dispatch(resetAuthTokenError());
      dispatch(resetAuthError());
    };
  }, [dispatch]);



  // Función para obtener las reglas de validación
  const computeTitle = useCallback(() => {
    switch (currentView) {
      case 'emailForgotPassword':
      case 'forgotPasswordOptions':
      case 'whatsappForgotPassword':
        return 'Olvidé mi contraseña 😔';
      case 'registerOptions':
      case 'whatsappRegister':
      case 'emailRegister':
        return 'Crear Cuenta 😃';
      default:
        return 'Iniciar Sesión';
    }
  }, [currentView]);

  useEffect(() => {
    onTitleChange?.(computeTitle());
  }, [computeTitle, onTitleChange]);

  const modalStyles = {
    body: {
      padding: '32px',
      // background: isDark
      //   ? 'linear-gradient(135deg,#1f1f1f 0%,#2c2c2c 100%)'
      //   : 'linear-gradient(135deg,#ffffff 0%,#f5f7fa 100%)'
    },
    header: {
      borderBottom: 'none',
      paddingBottom: '15px',
      paddingTop: '15px',
      color: isDark ? '#fff' : '#000',
      // background: isDark
      //   ? 'linear-gradient(135deg,#000428 0%,#004e92 100%)'
      //   : 'linear-gradient(135deg,#0099AF 0%,#00D5C7 100%)'
    },
    content: {
      borderRadius: radius,
      overflow: 'hidden',
      // boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
    },
    // mask: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
  } as const;

  return (
    <div style={modalStyles.content as React.CSSProperties}>
      {showHeader && (
        <div style={modalStyles.header as React.CSSProperties}>
          <div
            aria-live="polite"
            aria-atomic="true"
            style={{ textAlign: 'center', fontSize: '24px', fontWeight: 600 }}
          >
            {computeTitle()}
          </div>
        </div>
      )}

      <div style={modalStyles.body as React.CSSProperties}>
        <div ref={loginContainerRef} aria-busy={authStatus === 'loading'}>
          {/* Vista de inicio de sesión - NUEVO: SmartLoginForm con detección automática */}
          {currentView === 'login' && (
            <SmartLoginForm
              onForgotPassword={() => setCurrentView('forgotPasswordOptions')}
              onRegister={() => setCurrentView('registerOptions')}
              onWhatsAppLogin={() => setCurrentView('whatsappLogin')}
              buttonRadius={radius}
            />
          )}

          {/* Opciones de registro */}
          {currentView === 'registerOptions' && (
            <>
              <div className="w-100 d-flex justify-content-center my-4">
                <span style={{ fontSize: 18, fontWeight: 500 }}>✨ Regístrate con</span>
              </div>
              <Space orientation="vertical" className="w-100" size="middle">
                <Button
                  block
                  onClick={() => setCurrentView('whatsappRegister')}
                  icon={<WhatsAppOutlined />}
                  size="large"
                  shape={DEFAULT_BUTTON_SHAPE}
                  style={{ fontWeight: 500, height: 48, borderRadius: radius, color: 'black' }}
                  className="bg_color_whatsapp hover_web_color_claro_1 hover_border_black"
                >
                  WhatsApp
                </Button>
                <Button
                  block
                  onClick={() => setCurrentView('emailRegister')}
                  icon={<MailOutlined />}
                  size="large"
                  shape={DEFAULT_BUTTON_SHAPE}
                  style={{ fontWeight: 500, height: 48, borderRadius: radius }}
                  className="bg_verde_AV hover_web_color_claro_1 hover_border_black"
                >
                  Email
                </Button>
                <Button
                  type="text"
                  onClick={() => setCurrentView('login')}
                  className="bg-transparent border-0 hover_text_color_oscuro_3"
                  style={{ height: 40 }}
                >
                  ← Volver al inicio
                </Button>
              </Space>
            </>
          )}

          {/* Login/Register/Forgot con WhatsApp o Email (sin cambios) */}
          {currentView === 'whatsappLogin' && (
            <>
              {/* Componente WhatsAppLoginInput en modo login */}
              <WhatsAppLoginInput formMode="login" />
              <Button type="text" onClick={() => setCurrentView('login')} className="bg-transparent border-0 hover_text_color_oscuro_3">← Cancelar</Button>
            </>
          )}

          {/* Vista de registro con WhatsApp */}
          {currentView === 'whatsappRegister' && (
            <>
              {/* Componente WhatsAppLoginInput en modo registro */}
              <WhatsAppLoginInput formMode="register" />
              <Button type="text" onClick={() => setCurrentView('registerOptions')} className="bg-transparent border-0 hover_text_color_oscuro_3">← Cancelar</Button>
            </>
          )}

          {/* Vista de registro con Email */}
          {currentView === 'emailRegister' && (
            <>
              {/* Componente EmailLoginInput en modo registro */}
              <EmailLoginInput formMode="register" />
              <Button type="text" onClick={() => setCurrentView('registerOptions')} className="bg-transparent border-0 hover_text_color_oscuro_3">← Cancelar</Button>
            </>
          )}

          {/* Vista de opciones para "Olvidé mi contraseña" */}
          {currentView === 'forgotPasswordOptions' && (
            <>
              <div className="w-100 d-flex justify-content-center my-4">
                <span style={{ fontSize: 18, fontWeight: 500, width: 350 }}>🔑 Recuperar contraseña con</span>
              </div>
              {/*<div className="w-100 d-flex justify-content-center mb-2 px-2">*/}
              {/*  <Text type="secondary" style={{ textAlign: 'center' }}>*/}
              {/*    También podés reenviar el enlace de invitación si sos parte del staff invitado.*/}
              {/*  </Text>*/}
              {/*</div>*/}
              <Space orientation="vertical" className="w-100" size="middle">
                <Button
                  block
                  onClick={() => setCurrentView('whatsappForgotPassword')}
                  icon={<WhatsAppOutlined />}
                  size="large"
                  shape={DEFAULT_BUTTON_SHAPE}
                  style={{ fontWeight: 500, height: 48, borderRadius: radius, color: 'black' }}
                  className="bg_color_whatsapp hover_web_color_claro_1 hover_border_black"
                >
                  WhatsApp
                </Button>
                <Button
                  block
                  onClick={() => setCurrentView('emailForgotPassword')}
                  icon={<MailOutlined />}
                  size="large"
                  shape={DEFAULT_BUTTON_SHAPE}
                  style={{ fontWeight: 500, height: 48, borderRadius: radius }}
                  className="bg_verde_AV hover_web_color_claro_1 hover_border_black"
                >
                  Email
                </Button>
                <Button type="text" onClick={() => setCurrentView('login')} className="bg-transparent border-0 hover_text_color_oscuro_3">← Cancelar</Button>
              </Space>
            </>
          )}

          {/* Vista de recuperación de contraseña con WhatsApp */}
          {currentView === 'whatsappForgotPassword' && (
            <>
              {/* Componente WhatsAppLoginInput en modo cambio de contraseña */}
              <WhatsAppLoginInput formMode="pass_change" />
              <Button type="text" onClick={() => setCurrentView('forgotPasswordOptions')} className="bg-transparent border-0 hover_text_color_oscuro_3">← Cancelar</Button>
            </>
          )}

          {/* Vista de recuperación de contraseña con Email */}
          {currentView === 'emailForgotPassword' && (
            <>
              {/* Componente EmailLoginInput en modo cambio de contraseña */}
              <EmailLoginInput formMode="pass_change" />
              <Button type="text" onClick={() => setCurrentView('forgotPasswordOptions')} className="bg-transparent border-0 hover_text_color_oscuro_3">← Cancelar</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================
// Wrappers: Modal y Página
// ============================
interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { resolvedTheme } = useThemeMode();
  const radius = DEFAULT_BUTTON_RADIUS;
  const isDark = resolvedTheme === 'dark';

  const [modalTitle, setModalTitle] = useState<string>('Iniciar sesión');

  const modalStyles = {
    mask: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
    content: { borderRadius: radius, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' },
    header: {
      borderBottom: 'none',
      paddingBottom: 15,
      paddingTop: 15,
      color: isDark ? '#fff' : '#000',
      // background: isDark
      //   ? 'linear-gradient(135deg,#000428 0%,#004e92 100%)'
      //   : 'linear-gradient(135deg,#0099AF 0%,#00D5C7 100%)'
    },
    body: { padding: 0 }, // el padding lo maneja LoginContent
  } as const;

  const handleAfterClose = () => {
    // limpiar estado global al cerrar el modal
    dispatch(resetAuthTokenState());
    dispatch(resetAuthTokenError());
    dispatch(resetAuthError());
  };

  return (
    <Modal
      rootClassName="modern-login-modal"
      title={
        <div style={{ textAlign: 'center', fontSize: 24, fontWeight: 600 }}>
          {modalTitle}
        </div>
      }
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ maxWidth: 450 }}
      styles={modalStyles}
      destroyOnHidden
      afterClose={handleAfterClose}
    >
      <LoginContent
        onClose={onClose}
        animateKey={open}
        showHeader={false}
        onTitleChange={setModalTitle}
      />
    </Modal>
  );
};

const isSafeInternalPath = (raw?: string | null): string | undefined => {
  if (!raw) return undefined;
  try {
    // Evita URLs absolutas (open redirect). Forzamos paths relativos internos.
    if (/^https?:\/\//i.test(raw)) return undefined;
    // Normaliza doble slash inicial y evita que apunte al login
    const norm = raw.replace(/^\//, '/');
    if (!norm.startsWith('/')) return undefined;
    if (norm.startsWith('/auth/login')) return undefined;
    return norm;
  } catch {
    return undefined;
  }
};

// Utils (pueden ir arriba del archivo donde está LoginPage)
type LoginOriginState = { from?: Location | string; reason?: string; ts?: number };

const POST_LOGIN_KEY = 'postLoginRedirect';
const POST_LOGIN_TTL_MS = 30 * 60 * 1000; // 30 min de validez para evitar redirects viejos

const readPostLogin = () => {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { to?: string; reason?: string; ts?: number };
    if (!parsed?.to) return undefined;
    // Expiración (opcional, por seguridad)
    if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > POST_LOGIN_TTL_MS) {
      sessionStorage.removeItem(POST_LOGIN_KEY);
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
};

const consumePostLogin = () => {
  try {
    sessionStorage.removeItem(POST_LOGIN_KEY);
  } catch { /* noop */ }
};

// Asegúrate de tener ya definido isSafeInternalPath(raw?: string | null): string | undefined

const getPostLoginCandidate = (location: Location, opts?: { consume?: boolean }) => {
  const { consume = false } = opts ?? {};
  const st = (location.state as LoginOriginState) || {};
  const qs = new URLSearchParams(location.search || '');

  // 1) state.from (puede ser Location o string). Incluye pathname + search + hash
  if (st.from) {
    const s =
      typeof st.from === 'string'
        ? st.from
        : (st.from.pathname || '/') + (st.from.search || '') + (st.from.hash || '');
    const safe = isSafeInternalPath(s);
    if (safe) return { to: safe, reason: st.reason, source: 'state' as const };
  }

  // 2) querystring ?from=/ruta&reason=forbidden (acepta from url-encoded)
  const qsFromRaw = qs.get('from');
  const qsFrom = qsFromRaw ? (() => { try { return decodeURIComponent(qsFromRaw); } catch { return qsFromRaw; } })() : null;
  const safeQs = isSafeInternalPath(qsFrom);
  if (safeQs) return { to: safeQs, reason: qs.get('reason') || undefined, source: 'query' as const };

  // 3) sessionStorage (guardado por el interceptor de Axios)
  const stored = readPostLogin();
  if (stored) {
    const safe = isSafeInternalPath(stored.to || '');
    if (safe) {
      if (consume) consumePostLogin(); // opcional: consumir aquí si querés one-shot
      return { to: safe, reason: stored.reason, source: 'storage' as const };
    }
  }

  return { to: undefined, reason: undefined, source: undefined };
};

const rememberPostLogin = (to?: string, reason?: string) => {
  try {
    if (!to) return;
    sessionStorage.setItem(
      'postLoginRedirect',
      JSON.stringify({ to, reason, ts: Date.now() }),
    );
  } catch { /* noop */ }
};

// Render de página completa reutilizando el mismo contenido
export const LoginPage: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state: RootState) => state.auth);
  const siteSettings = useAppSelector((state: RootState) => state.settings.site);

  const navigate = useNavigate();
  const location = useLocation();

  logInfo('LoginPage - location: ', location);

  // Derivar nombre del sitio
  const siteName = siteSettings?.name?.trim() || null;

  // Derivar URL del logo
  const logoUrl = siteSettings?.logo?.thumbnails?.default || null;

  /* ----------------------------- Efectos ---------------------------------- */
  // Al montar el Login, si sabemos desde dónde vino, lo recordamos
  useEffect(() => {
    const { to, reason } = getPostLoginCandidate(location);
    if (to) rememberPostLogin(to, reason);
    // solo en mount / cambios de url explícitos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Al autenticarse, intentamos volver al destino original una sola vez
  useEffect(() => {
    if (!isAuthenticated) return;
    const { to } = getPostLoginCandidate(location);
    const target = to || '/';
    consumePostLogin();
    navigate(target, { replace: true });
  }, [isAuthenticated, navigate, location]);

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 0px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        gap: 24,
      }}
    >
      {/* Header con logo y nombre - estilo minimalista */}
      {(logoUrl || siteName) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            marginBottom: 8,
          }}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt={siteName || 'Logo'}
              style={{
                maxHeight: 64,
                maxWidth: 200,
                objectFit: 'contain',
              }}
            />
          )}
          {siteName && (
            <span
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: 'var(--web-color-1, var(--ant-color-text))',
                textAlign: 'center',
              }}
            >
              {siteName}
            </span>
          )}
        </div>
      )}

      <LoginContent
        showHeader
        animateKey="page"
      />
    </div>
  );
};


export const LogoutPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { status } = useAppSelector((state) => state.auth);
  const siteSettings = useAppSelector((state: RootState) => state.settings.site);

  // Derivar nombre del sitio
  const siteName = siteSettings?.name?.trim() || null;

  // Derivar URL del logo
  const logoUrl = siteSettings?.logo?.thumbnails?.default || null;

  // 2) Efecto que se dispara solo al montar el componente
  useEffect(() => {
    const doLogout = async () => {
      try {
        await dispatch(logout()).unwrap();        // ← tu petición de logout
      } finally {
        navigate('/auth/login', { replace: true });
      }
    };

    void doLogout();
  }, [dispatch, navigate]);

  return (
    <div>
      {/* Header con logo y nombre - estilo minimalista */}
      {(logoUrl || siteName) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            marginBottom: 8,
          }}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt={siteName || 'Logo'}
              style={{
                maxHeight: 64,
                maxWidth: 200,
                objectFit: 'contain',
              }}
            />
          )}
          {siteName && (
            <span
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: 'var(--web-color-1, var(--ant-color-text))',
                textAlign: 'center',
              }}
            >
              {siteName}
            </span>
          )}
        </div>
      )}
      <h1>Adios!</h1>
      <p>Nos vemos luego</p>
      <Spin spinning={status === 'loading'} />
    </div>
  );
};
